// frontend/src/components/billing/PlanCard.jsx
//
// The billing card of the Plans page, and the parts of it the SaaS paywall
// (PricingGate) shows in its popup, so the two read as one product:
//
//   <PlanCard>   — the page's frame: icon and heading, one sentence, the
//                  body, and the "Questions? Talk to us" footer.
//   <LoadingCard>, <RetryCard>, <NotReadyCard>
//                — the same frame while the price loads, when its read
//                  failed, and when the store cannot be priced yet.
//   <PlanOffer>  — one plan the way the public pricing page shows it: the
//                  plan's name ("Pricing plan: Core"), the Monthly / Annual
//                  toggle (only where the merchant is choosing an interval),
//                  the price, the revenue band, and what every plan includes.
//   <PlanHelp>   — the "Questions? Talk to us" line; the card closes with it
//                  as a band, the popup's content ends with it as a line.
//
// The page puts its action under the offer in a `.plan-card-cta`; the paywall
// puts it in its popup's button bar.
//
// Every figure comes off the `prices` block of `/api/billing/pricing/`;
// the interval arithmetic is `billing/money.intervalPrices`.

import { useTranslation } from 'react-i18next';
import { Button, CircularProgress } from '@mui/material';
import { ICON, Check, WarningCircle } from '../../icons';
import { useFormatters } from '../../hooks/useFormatters';
import { intervalPrices, usd } from '../../billing/money';
import ContactSupportButton from '../common/ContactSupportButton';
import { FEATURE_KEYS } from './planCopy';
import { trackEvent } from '../../hooks/useTracking';
import './PlanCard.css';

/** The way to a person, under every price and every failure. */
export function PlanHelp() {
  const { t } = useTranslation();
  return (
    <p className="plan-help">
      {t('billing.questions')} <a href="https://manexys.com/contact" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('contact_page_opened')}>{t('billing.talkToUs')}</a>
    </p>
  );
}

/**
 * @param {import('react').ElementType} icon  a Phosphor icon
 * @param {string} heading
 * @param {string|null} [subtitle]
 */
export function PlanCard({ icon: Icon, heading, subtitle, children }) {
  return (
    <div className="plan-card">
      <div className="plan-card-header">
        <Icon size={ICON.xxl} weight="fill" className="plan-card-header-icon" />
        <h2>{heading}</h2>
      </div>
      {subtitle && <p className="plan-card-subtitle">{subtitle}</p>}
      {children}
      <div className="plan-card-help"><PlanHelp /></div>
    </div>
  );
}

/** The card while the price loads. */
export function LoadingCard() {
  return (
    <div className="plan-card">
      <div className="plan-card-loading">
        <CircularProgress size={28} />
      </div>
    </div>
  );
}

/** A card with a heading, a sentence and one retry button. */
export function RetryCard({ title, body, onRetry }) {
  const { t } = useTranslation();
  return (
    <PlanCard icon={WarningCircle} heading={title} subtitle={body}>
      <div className="plan-card-cta">
        <Button variant="contained" onClick={onRetry}>{t('billing.retry')}</Button>
      </div>
    </PlanCard>
  );
}

/**
 * The store cannot be priced (the pricing read answered 503). Try again, or
 * write to support — a merchant whose revenue cannot be read has no other way
 * forward, and the window does not reopen while they wait.
 */
export function NotReadyCard({ onRetry }) {
  const { t } = useTranslation();
  return (
    <PlanCard
      icon={WarningCircle}
      heading={t('billing.notReady.title')}
      subtitle={t('billing.notReady.body')}
    >
      <div className="plan-card-cta">
        <Button variant="contained" onClick={onRetry}>{t('billing.retry')}</Button>
        <ContactSupportButton variant="outlined" />
      </div>
    </PlanCard>
  );
}

function IntervalToggle({ isAnnual, onIntervalChange }) {
  const { t } = useTranslation();
  return (
    <div className="plan-offer-toggle">
      <button
        type="button"
        className="plan-offer-toggle-btn"
        aria-pressed={!isAnnual}
        onClick={() => onIntervalChange(false)}
      >
        {t('billing.monthlyToggle')}
      </button>
      <button
        type="button"
        className="plan-offer-toggle-btn"
        aria-pressed={isAnnual}
        onClick={() => onIntervalChange(true)}
      >
        {t('billing.annualToggle')}
      </button>
    </div>
  );
}

function OfferPrice({ prices, isAnnual }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const { tier } = prices;
  const billedAnnual = Number(prices.annual);
  const { perMonth, strikethrough } = intervalPrices({
    monthly: Number(prices.monthly), annual: billedAnnual, isAnnual,
  });
  return (
    <>
      <div className="plan-offer-amount">
        {strikethrough && (
          <span className="plan-offer-amount-original">{usd(strikethrough, formatNumber)}</span>
        )}
        <span className="plan-offer-amount-value">{usd(perMonth, formatNumber)}</span>
        <span className="plan-offer-amount-period">{t('billing.perMonth')}</span>
      </div>
      <p className="plan-offer-revenue">
        {t(`billing.tierRevenue.${tier}`)}.
        <br />
        {isAnnual
          ? t('billing.billedAnnually', { total: usd(billedAnnual, formatNumber) })
          : t('billing.billedMonthly')}
      </p>
    </>
  );
}

/**
 * @param {object} prices              `prices` off the pricing payload
 * @param {boolean} isAnnual           the selected interval
 * @param {Function} [onIntervalChange]  called with the new `isAnnual`;
 *                                    without it there is no toggle — the
 *                                    interval is the one the merchant pays
 * @param {import('react').ReactNode} [note]   under the price
 */
export function PlanOffer({ prices, isAnnual, onIntervalChange, note }) {
  const { t } = useTranslation();
  const { tier } = prices;
  return (
    <div className="plan-offer">
      <div className="plan-offer-name">
        <h3>{t('billing.plans.pricingPlan', { plan: prices.tier_display })}</h3>
      </div>

      {/* An unquoted Enterprise band has no price: the numbers are agreed
          with sales, so there is no interval to pick either. */}
      {onIntervalChange && !prices.contact_sales && (
        <IntervalToggle isAnnual={isAnnual} onIntervalChange={onIntervalChange} />
      )}

      <div className="plan-offer-price">
        {prices.contact_sales ? (
          <>
            <div className="plan-offer-amount">
              <span className="plan-offer-amount-value">{t('billing.plans.customPricing')}</span>
            </div>
            <p className="plan-offer-revenue">{t(`billing.tierRevenue.${tier}`)}.</p>
          </>
        ) : (
          <OfferPrice prices={prices} isAnnual={isAnnual} />
        )}
        {note && <p className="plan-offer-note">{note}</p>}
      </div>

      <div className="plan-offer-features">
        {FEATURE_KEYS.map((key) => (
          <div key={key} className="plan-offer-feature">
            <Check size={ICON.md} weight="bold" className="plan-offer-feature-icon" />
            <span>{t(`billing.features.${key}`)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
